import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'

import { observer } from 'mobx-react'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import DirectUploader from 'modules/trainings/components/Files/Uploaders/DirectUploader'
import { ContentEditorProps } from 'modules/trainings/components/content/types'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'
import { PageBlock } from 'modules/trainings/lib/content/PageBlock'

export const PageBlockEditor = observer(
  ({ contentItem, trainingId }: ContentEditorProps<PageBlock>) => {
    const descriptionRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)
    const {
      editorConfig,
      canUseRichTextEditor,
      loading: richTextEditorLoading,
    } = useRichTextEditor()

    // Create a File object from existing thumbnail for display
    const initialFile = contentItem.thumbnail.isEmpty()
      ? undefined
      : contentItem.thumbnail.toFileObject('image/jpeg')

    return (
      <>
        <Input
          label={i18n.t('trainings.content.block.page.title_label')}
          onChange={(value: string) => {
            contentItem.title = value
          }}
          placeholder={i18n.t('trainings.content.block.page.title_placeholder')}
          value={contentItem.title}
          required
        />

        <Box className="gap-2">
          <Box className="gap-1">
            <Text size="200" className="text-f1-foreground-secondary" weight="medium">
              {i18n.t('trainings.content.block.page.thumbnail_label')}
            </Text>
            <Text size="100" className="text-base text-f1-foreground-secondary">
              {i18n.t('trainings.content.block.page.thumbnail_description')}
            </Text>
          </Box>
          <DirectUploader
            onUpload={async (uploadData) => {
              contentItem.thumbnail = AttachedFile.fromUpload(
                uploadData.file,
                uploadData.signedId || ''
              )
            }}
            onDelete={() => {
              contentItem.thumbnail.clear()
            }}
            description={i18n.t('trainings.content.block.page.thumbnail_description')}
            fileTypes={['image' as const]}
            resourceType={'Trainings::Training'}
            resourceId={String(trainingId)}
            initialFile={initialFile}
          />
        </Box>

        <Box className="gap-2">
          <Box style={{ position: 'relative', overflow: 'hidden' }}>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <Box gap="s8">
                <Box flexDirection="row" gap="s4">
                  <Text size="200" className="text-f1-foreground-secondary" weight="medium">
                    {i18n.t('trainings.content.block.page.description_label')}
                  </Text>
                  <Text size="200" className="text-f1-foreground-critical">
                    {'*'}
                  </Text>
                </Box>
                <RichTextEditor
                  ref={descriptionRichTextEditorRef}
                  {...editorConfig}
                  onChange={(data) => {
                    contentItem.description = data?.value || ''
                  }}
                  placeholder={i18n.t('trainings.content.block.page.description_placeholder')}
                  initialEditorState={{ content: contentItem.description || '' }}
                  title={i18n.t('trainings.content.block.page.description_label')}
                  fullScreenMode
                />
              </Box>
            ) : (
              <Textarea
                label={i18n.t('trainings.content.block.page.description_label')}
                value={contentItem.description}
                onChange={(value) => {
                  contentItem.description = value
                }}
                placeholder={i18n.t('trainings.content.block.page.description_placeholder')}
              />
            )}
          </Box>
        </Box>
      </>
    )
  }
)
